/**
 * deriveVitalType — the HONEST vital-TYPE inference unit. PURE + data-driven.
 *
 * op_fact_observation has NO human-readable LOINC name (op_dim_code is ABSENT; D-32 forbids reaching
 * into gold), so we NEVER invent a clinical name. Instead we infer the measure type ONLY from the real
 * columns the row actually carries:
 *   1. component_systolic AND component_diastolic present -> "Blood pressure"
 *   2. else by value_unit (case-insensitive, exact token): lbs|kg -> Weight; in|cm -> Height;
 *      Index|kg/m2 -> BMI; % -> "Percentage (%)" (NEVER guessed as O2 sat); /min|bpm -> "Rate (/min)";
 *      F|C|degF|degC -> Temperature; mmHg -> Pressure
 *   3. else free-text/unstructured (value_string present and is_coded_flag false, OR
 *      gap_reason='unstructured_documentation') -> "Unstructured note" (badged partial; NEVER trended)
 *   4. else -> "Coded vital" (a real coded obs whose type op_* can't map; surface the short surrogate +
 *      the honest "no dim_code" tooltip, never a faked name)
 *
 * Each distinct code_key is a distinct measure; the UI groups by code_key and labels each group from
 * the inferred type of its representative (most-recent) row.
 */

/** Discriminates how a group renders: numeric kinds trend; bp dual-trends; the rest never trend. */
export type VitalKind =
  | "bp"
  | "weight"
  | "height"
  | "bmi"
  | "percentage"
  | "rate"
  | "temperature"
  | "pressure"
  | "unstructured"
  | "coded";

export interface VitalTypeInfo {
  label: string;
  kind: VitalKind;
}

/** Minimal-necessary row shape (a structural subset of contracts.Vital) so this stays dependency-free. */
export interface VitalTypeInput {
  value_unit?: string | null;
  value_string?: string | null;
  component_systolic?: number | null;
  component_diastolic?: number | null;
  is_coded_flag?: boolean | null;
  gap_reason?: string | null;
}

/** Numeric kinds carry a real value_quantity (or BP components) and are eligible for trending. */
export function isNumericKind(kind: VitalKind): boolean {
  return kind !== "unstructured";
}

/** value_unit (lowercased, trimmed) -> { label, kind }. Exact-token match; no fuzzy guessing. */
const UNIT_MAP: Record<string, VitalTypeInfo> = {
  lbs: { label: "Weight", kind: "weight" },
  kg: { label: "Weight", kind: "weight" },
  in: { label: "Height", kind: "height" },
  cm: { label: "Height", kind: "height" },
  index: { label: "BMI", kind: "bmi" },
  "kg/m2": { label: "BMI", kind: "bmi" },
  "%": { label: "Percentage (%)", kind: "percentage" },
  "/min": { label: "Rate (/min)", kind: "rate" },
  bpm: { label: "Rate (/min)", kind: "rate" },
  f: { label: "Temperature", kind: "temperature" },
  c: { label: "Temperature", kind: "temperature" },
  "°f": { label: "Temperature", kind: "temperature" },
  "°c": { label: "Temperature", kind: "temperature" },
  degf: { label: "Temperature", kind: "temperature" },
  degc: { label: "Temperature", kind: "temperature" },
  mmhg: { label: "Pressure", kind: "pressure" },
};

export function deriveVitalType(row: VitalTypeInput): VitalTypeInfo {
  // 1. Blood pressure — both components present (most specific, beats any unit reading).
  if (row.component_systolic != null && row.component_diastolic != null) {
    return { label: "Blood pressure", kind: "bp" };
  }

  // 2. Unit-keyed inference (case-insensitive, exact token).
  const unit = row.value_unit?.trim().toLowerCase();
  if (unit) {
    const mapped = UNIT_MAP[unit];
    if (mapped) return mapped;
  }

  // 3. Free-text / unstructured — a value_string that is not a coded vital.
  const isUnstructured =
    (row.value_string != null && row.value_string !== "" && row.is_coded_flag === false) ||
    row.gap_reason === "unstructured_documentation";
  if (isUnstructured) {
    return { label: "Unstructured note", kind: "unstructured" };
  }

  // 4. A real coded observation whose type op_* cannot map (no dim_code) — honest fallback, never faked.
  return { label: "Coded vital", kind: "coded" };
}
